<template>
  <div class="welcome-generator-container">
    <div class="card">
      <h2 class="title">欢迎图片生成器</h2>
      
      <!-- 背景图选择区域 -->
      <div class="bg-selector-section">
        <label>选择背景模板</label>
        <div class="bg-scroll-wrapper">
          <div 
            v-for="(bg, index) in backgroundList" 
            :key="index"
            class="bg-item"
            :class="{ active: selectedBgIndex === index }"
            @click="selectedBgIndex = index"
          >
            <img :src="bg.url" :alt="'背景 ' + (index + 1)" />
            <div v-if="selectedBgIndex === index" class="active-badge">✓</div>
          </div>
        </div>
      </div>

      <!-- 输入区域 -->
      <div class="input-section">
        <label for="customerName">客户名称</label>
        <input 
          id="customerName"
          type="text" 
          v-model="customerName" 
          placeholder="客户名称，如:湖南长沙李总"
          :disabled="isGenerating"
        />
        <button 
          class="generate-btn" 
          @click="generateImage" 
          :disabled="!customerName || isGenerating"
        >
          {{ isGenerating ? '生成中...' : '生成欢迎图片' }}
        </button>
      </div>

      <!-- 错误提示 -->
      <div v-if="errorMessage" class="error-msg">
        {{ errorMessage }}
      </div>

      <!-- 图片预览与下载区域 -->
      <div v-if="imageUrl" class="preview-section">
        <div class="image-wrapper" @click="openPreview">
          <img :src="imageUrl" alt="Generated Welcome Image" />
          <div class="zoom-hint">点击放大</div>
        </div>
        <button class="download-btn" @click="downloadImage">
          下载图片
        </button>
      </div>
    </div>

    <!-- 全屏放大预览层 -->
    <div v-if="showPreview" class="fullscreen-preview" @click="closePreview">
      <span class="close-preview" @click.stop="closePreview">✕</span>
      <img :src="imageUrl" alt="Generated Welcome Image" @click.stop />
      <div class="save-hint">长按图片可保存到相册</div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { BASE_URL } from '@/config'; 

// 背景图列表配置
const backgroundList = [
  { url: 'https://resource.youlangtaofang.com/jp/base0_min.jpg' },
  { url: 'https://resource.youlangtaofang.com/jp/base1_min.jpg' },
  { url: 'https://resource.youlangtaofang.com/jp/base2_min.jpg' }
];

const customerName = ref('');
const imageUrl = ref('');
const isGenerating = ref(false);
const errorMessage = ref('');
const selectedBgIndex = ref(0); // 默认选中第一个背景
const showPreview = ref(false); // 控制全屏放大预览层

// 打开全屏预览
const openPreview = () => {
  if (!imageUrl.value) return;
  showPreview.value = true;
};

// 关闭全屏预览
const closePreview = () => {
  showPreview.value = false;
};

// 生成图片
const generateImage = async () => {
  if (!customerName.value.trim()) {
    errorMessage.value = '请输入客户名称';
    return;
  }

  isGenerating.value = true;
  errorMessage.value = '';
  imageUrl.value = ''; // 清空旧图片

  try {
    // 构造 URL，参数放在查询字符串中
    const apiUrl = `${BASE_URL}/ClientApi/GenerateWelcomeImage?customerName=${encodeURIComponent(customerName.value)}&bgIndex=${selectedBgIndex.value}`;
    
    // 调用接口，使用 POST 方法，不带 Body
    const response = await fetch(apiUrl, {
      method: 'POST',
      // 注意：这里没有 body，参数都在 URL 里
    });

    if (!response.ok) {
      throw new Error(`生成失败: ${response.status} ${response.statusText}`);
    }

    // 解析 JSON 响应
    const result = await response.json();

    if (result.code !== 200 || !result.data?.url) {
      throw new Error(result.message || '生成失败，返回数据格式不正确');
    }

    // 直接使用接口返回的图片 URL
    imageUrl.value = result.data.url;

  } catch (error) {
    console.error('Generate image error:', error);
    errorMessage.value = error.message || '网络请求失败，请稍后重试';
  } finally {
    isGenerating.value = false;
  }
};

// 下载图片
const downloadImage = async () => {
  if (!imageUrl.value) return;

  try {
    // 跨域图片需先取回为 blob，否则 a.download 会被忽略而变成跳转
    const response = await fetch(imageUrl.value);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    const fileName = `欢迎图_${customerName.value}_${new Date().getTime()}.jpg`;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // 释放临时 URL
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download image error:', error);
    errorMessage.value = '图片下载失败，请尝试长按图片保存';
  }
};
</script>

<style scoped>
.welcome-generator-container {
 vertical-align: top;
 overflow-y:scroll;
  min-height: 100vh;
  background-color: #f5f7fa;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.card {
  background: #fff;
  width: 100%;
  max-width: 600px;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  text-align: center;
}

.title {
  margin-bottom: 25px;
  color: #333;
  font-size: 24px;
}

/* --- 背景选择器样式 --- */
.bg-selector-section {
  margin-bottom: 25px;
  text-align: left;
}

.bg-selector-section label {
  display: block;
  font-weight: bold;
  color: #555;
  margin-bottom: 10px;
  font-size: 14px;
}

.bg-scroll-wrapper {
  display: flex;
  gap: 15px;
  overflow-x: auto;
  padding-bottom: 10px; /* 为滚动条留出空间 */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: #ccc transparent;
}

/* Webkit 滚动条样式 */
.bg-scroll-wrapper::-webkit-scrollbar {
  height: 6px;
}
.bg-scroll-wrapper::-webkit-scrollbar-track {
  background: transparent;
}
.bg-scroll-wrapper::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 3px;
}

.bg-item {
  flex: 0 0 auto; /* 防止压缩 */
  width: 100px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s ease;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.bg-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 选中状态 */
.bg-item.active {
  border-color: #ff4d4f; /* 红色边框 */
  box-shadow: 0 0 8px rgba(255, 77, 79, 0.4);
  transform: scale(1.05);
}

.active-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  background: #ff4d4f;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* --- 原有样式 --- */
.input-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
}

label {
  text-align: left;
  font-weight: bold;
  color: #555;
  margin-bottom: 5px;
}

input {
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.3s;
}

input:focus {
  border-color: #409eff;
}

input:disabled {
  background-color: #f5f7fa;
  cursor: not-allowed;
}

.generate-btn {
  padding: 12px;
  background-color: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.generate-btn:hover:not(:disabled) {
  background-color: #66b1ff;
}

.generate-btn:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}

.error-msg {
  color: #f56c6c;
  margin-bottom: 15px;
  font-size: 14px;
}

.preview-section {
  margin-top: 20px;
  border-top: 1px solid #eee;
  padding-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.image-wrapper {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  background-color: #fafafa;
  min-height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  cursor: zoom-in;
}

.zoom-hint {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 14px;
  pointer-events: none;
}

.image-wrapper img {
  max-width: 100%;
  height: auto;
  display: block;
}

.download-btn {
  padding: 10px 25px;
  background-color: #67c23a;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.download-btn:hover {
  background-color: #85ce61;
}

/* --- 全屏放大预览层 --- */
.fullscreen-preview {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
}

.fullscreen-preview img {
  max-width: 100%;
  max-height: 85%;
  object-fit: contain;
  border-radius: 4px;
  /* 允许浏览器原生长按菜单(保存图片) */
  -webkit-touch-callout: default;
  user-select: none;
}

.close-preview {
  position: absolute;
  top: 16px;
  right: 20px;
  color: #fff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  z-index: 1;
  padding: 6px;
}

.save-hint {
  margin-top: 16px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
}
</style>